import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { userService } from '../services/userService';
import { projectService } from '../services/projectService';
import { Button, Input, Heading, Text } from './ui';
import HouseGlyph from './HouseGlyph';
import '../styles/Login.css';

const LockIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="11" width="18" height="10" rx="2" ry="2"></rect>
    <path d="M7 11V7a5 5 0 0110 0v4"></path>
  </svg>
);

function PreviewCode({ user, onComplete }) {
  const [previewCode, setPreviewCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [settingUpAccount, setSettingUpAccount] = useState(false);
  const navigate = useNavigate();

  const handleSubmitCode = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await userService.validatePreviewCode(previewCode);

      if (result.valid) {
        // Create the document WITHOUT access — redeemPreviewCode performs the
        // grant after re-validating the code itself. Granting is server-owned
        // so a client cannot walk itself past the waitlist.
        await userService.createUser(user.uid, {
          email: user.email,
          displayName: user.displayName || '',
          photoURL: user.photoURL || '',
          hasPreviewAccess: false,
          joinedWaitlistAt: new Date(),
        });

        // Redeem: records WHICH code was used and grants access. Reusable
        // codes are reported too — the server decides whether to burn.
        let granted = false;
        try {
          const redemption = await userService.redeemPreviewCode(previewCode);
          granted = redemption.valid;
        } catch (error) {
          console.error('Error redeeming preview code:', error);
        }

        if (!granted) {
          // Validated a moment ago but not redeemable now — most likely a
          // single-use code someone else just spent.
          setError(
            "We couldn't apply that code. Please try again or request a new one."
          );
          setLoading(false);
          return;
        }

        // Show setting up account message and wait for demo project to be copied
        setSettingUpAccount(true);

        // Poll for demo project to appear (max 10 seconds)
        // The copy takes ~14 s in practice (measured on prod: 13.8 s and 14.1 s),
        // so a 10 s budget timed out on every signup and dropped the user into an
        // empty project list. Polling exits the moment the project appears, so a
        // larger ceiling costs a fast copy nothing.
        const maxAttempts = 60; // 60 attempts * 500ms = 30 seconds max
        let attempts = 0;

        const checkForDemoProject = async () => {
          try {
            const projects = await projectService.getProjects();
            if (projects && projects.length > 0) {
              // Demo project has been created, proceed
              console.log('Demo project found after', attempts + 1, 'attempts');
              if (onComplete) {
                onComplete(true);
              } else {
                navigate('/');
              }
              return;
            }
          } catch (error) {
            console.error('Error checking for demo project:', error);
          }

          attempts++;
          if (attempts < maxAttempts) {
            // Try again after 500ms
            setTimeout(checkForDemoProject, 500);
          } else {
            // Timeout, proceed anyway
            console.log('Demo project check timed out, proceeding anyway');
            if (onComplete) {
              onComplete(true);
            } else {
              navigate('/');
            }
          }
        };

        // Start checking after a brief initial delay to allow Cloud Function to trigger
        setTimeout(checkForDemoProject, 1000);
      } else {
        setError(
          result.retired
            ? 'This code is no longer active. Please request a new one.'
            : 'Invalid preview code. Please try again.'
        );
        // Add shake animation
        const input = document.getElementById('previewCode');
        input.classList.add('shake');
        setTimeout(() => input.classList.remove('shake'), 500);
      }
    } catch (error) {
      console.error('Error validating preview code:', error);
      setError('Unable to validate code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleJoinWaitlist = async () => {
    setLoading(true);

    try {
      // Create user document without preview access
      await userService.createUser(user.uid, {
        email: user.email,
        displayName: user.displayName || '',
        photoURL: user.photoURL || '',
        hasPreviewAccess: false,
        joinedWaitlistAt: new Date(),
      });

      if (onComplete) {
        onComplete(false);
      } else {
        navigate('/waitlist');
      }
    } catch (error) {
      setError('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Show setting up account message if we're in the process
  if (settingUpAccount) {
    return (
      <div className="login-container">
        <div className="login-box">
          <div className="logo-container">
            <img src="/FullLogo.png" alt="Housfully" className="logo" />
          </div>
          <Heading level={1} tone="dark" className="preview-title">
            Welcome to Housfully!
          </Heading>
          <Text tone="dark" className="subtitle">
            Setting up your account...
          </Text>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              marginTop: '30px',
              marginBottom: '20px',
            }}
          >
            <HouseGlyph
              stroke="#F3E9D2"
              style={{ width: '50px', height: '50px' }}
            />
          </div>
          <Text
            tone="dark"
            size="base"
            style={{
              textAlign: 'center',
            }}
          >
            Preparing your demo project...
          </Text>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container">
      <div className="login-box">
        <div className="logo-container">
          <img src="/FullLogo.png" alt="Housfully" className="logo" />
        </div>
        <Heading level={1} tone="dark" className="preview-title">
          Preview Edition
        </Heading>
        <Text tone="dark" className="subtitle">
          Enter your preview code to get early access
        </Text>

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmitCode} className="login-form">
          <Input
            tone="dark"
            type="text"
            id="previewCode"
            value={previewCode}
            onChange={(e) => setPreviewCode(e.target.value.toUpperCase())}
            placeholder="Enter preview code"
            disabled={loading}
            autoComplete="off"
            style={{ textTransform: 'uppercase' }}
            leadingIcon={LockIcon}
          />

          <Button
            type="submit"
            tone="dark"
            variant="primary"
            fullWidth
            loading={loading}
            disabled={loading || !previewCode}
          >
            {loading ? 'Verifying...' : 'Verify Code'}
          </Button>
        </form>

        <div className="divider-text">
          <p>Don't have a code?</p>
        </div>

        <button
          type="button"
          className="waitlist-button"
          onClick={handleJoinWaitlist}
          disabled={loading}
        >
          Join Waitlist
        </button>
      </div>
    </div>
  );
}

export default PreviewCode;
